import { useCallback, useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { useAuth } from "../../context/AuthContext";
import {
  SUBSCRIPTION_TIERS,
  TIER_ORDER,
  formatTierDollars,
  possessiveName,
} from "../../constants/subscriptionTiers";
import {
  confirmDropUnlock,
  createDropUnlockCheckout,
} from "../../services/dropUnlockService";
import "./DropUnlockChoiceModal.css";

const stripePromiseCache = new Map();

function getStripePromise(publishableKey) {
  const key =
    publishableKey
    || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
    || "";
  if (!key) return null;
  if (!stripePromiseCache.has(key)) {
    stripePromiseCache.set(key, loadStripe(key));
  }
  return stripePromiseCache.get(key);
}

function priceLabelFromCents(cents) {
  const n = Number(cents) || 0;
  return `$${(n / 100).toFixed(2)}`;
}

function PaymentForm({ email, onReady, onStripeRefs }) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    onStripeRefs?.({ stripe, elements });
    onReady?.(Boolean(stripe && elements));
  }, [stripe, elements, onReady, onStripeRefs]);

  return (
    <div className="duc-stripe-mount">
      <PaymentElement
        options={{
          layout: "tabs",
          defaultValues: {
            billingDetails: { email: email || undefined },
          },
        }}
      />
    </div>
  );
}

/**
 * Unlock payment modal.
 * - default: choice (one-time unlock vs subscribe)
 * - directPayOnly: skip choice, open Payment Element immediately (cash / archive)
 */
export default function DropUnlockChoiceModal({
  isOpen,
  onDismiss,
  drop,
  creatorName = "Creator",
  onUnlocked,
  onChooseSubscribe,
  directPayOnly = false,
  kind = "unlock", // unlock | archive
  clientSecret: seedClientSecret = null,
  paymentIntentId: seedPaymentIntentId = null,
  amountCents: seedAmountCents = null,
  publishableKey: seedPublishableKey = null,
}) {
  const { currentUser, refreshUserProfile } = useAuth();
  const [phase, setPhase] = useState("choice"); // choice | payment
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [amountCents, setAmountCents] = useState(0);
  const [publishableKey, setPublishableKey] = useState(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
  );
  const [paymentReady, setPaymentReady] = useState(false);
  const [stripeRef, setStripeRef] = useState({ stripe: null, elements: null });
  const [autoStarted, setAutoStarted] = useState(false);

  const dropId = drop?.id;
  const priceCad = kind === "archive"
    ? (Number(drop?.archivePrice ?? drop?.price) || 0)
    : (Number(drop?.price) || 0);
  const priceText = `$${priceCad.toFixed(2)}`;
  const firstName = String(creatorName || "Creator").split(" ")[0];

  useEffect(() => {
    if (!isOpen) {
      setAutoStarted(false);
      return;
    }
    setBusy(false);
    setError("");
    setPaymentReady(false);
    if (seedClientSecret) {
      setClientSecret(seedClientSecret);
      setPaymentIntentId(seedPaymentIntentId || null);
      setAmountCents(
        typeof seedAmountCents === "number"
          ? seedAmountCents
          : Math.round(priceCad * 100)
      );
      if (seedPublishableKey) setPublishableKey(seedPublishableKey);
      setPhase("payment");
      return;
    }
    setClientSecret(null);
    setPaymentIntentId(null);
    setAmountCents(Math.round(priceCad * 100));
    setPhase(directPayOnly ? "payment" : "choice");
  }, [
    isOpen,
    priceCad,
    dropId,
    directPayOnly,
    seedClientSecret,
    seedPaymentIntentId,
    seedAmountCents,
    seedPublishableKey,
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onDismiss?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, busy, onDismiss]);

  const stripePromise = useMemo(
    () => getStripePromise(publishableKey),
    [publishableKey]
  );

  const handleDismiss = () => {
    if (busy) return;
    onDismiss?.();
  };

  const startSingleUnlock = useCallback(async () => {
    if (!currentUser || !dropId) {
      setError("Sign in to unlock this drop.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await createDropUnlockCheckout({ dropId, kind });
      if (data.publishableKey) setPublishableKey(data.publishableKey);
      if (typeof data.amount === "number") setAmountCents(data.amount);

      if (data.alreadyUnlocked || data.quickPaid) {
        refreshUserProfile?.(currentUser.uid);
        onUnlocked?.({ dropId, alreadyUnlocked: Boolean(data.alreadyUnlocked), kind });
        onDismiss?.();
        return;
      }
      if (!data.clientSecret) {
        throw new Error("Payment could not be started.");
      }
      setClientSecret(data.clientSecret);
      setPaymentIntentId(data.paymentIntentId || null);
      setPhase("payment");
    } catch (err) {
      setError(err.message || "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  }, [currentUser, dropId, kind, onDismiss, onUnlocked, refreshUserProfile]);

  useEffect(() => {
    if (!isOpen || !directPayOnly || seedClientSecret || autoStarted) return;
    if (phase !== "payment" || clientSecret) return;
    setAutoStarted(true);
    startSingleUnlock();
  }, [
    isOpen,
    directPayOnly,
    seedClientSecret,
    autoStarted,
    phase,
    clientSecret,
    startSingleUnlock,
  ]);

  const confirmPayment = async () => {
    const { stripe, elements } = stripeRef;
    if (!stripe || !elements) {
      setError("Payment form is still loading.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: typeof window !== "undefined" ? window.location.href : undefined,
        },
      });
      if (stripeError) {
        throw new Error(stripeError.message || "Payment failed.");
      }
      const piId = paymentIntent?.id || paymentIntentId;
      if (!piId) throw new Error("Missing payment confirmation.");

      await confirmDropUnlock({ paymentIntentId: piId, dropId });
      refreshUserProfile?.(currentUser.uid);
      onUnlocked?.({
        dropId,
        paymentIntentId: piId,
        amountCents: paymentIntent?.amount || amountCents,
      });
      onDismiss?.();
    } catch (err) {
      setError(err.message || "Payment could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSubscribeTier = useCallback(
    (tierId) => {
      if (busy) return;
      onChooseSubscribe?.(tierId);
    },
    [busy, onChooseSubscribe]
  );

  if (!isOpen) return null;

  return (
    <div className="duc-overlay" onClick={handleDismiss} role="presentation">
      <div
        className="duc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="duc-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="duc-close"
          onClick={handleDismiss}
          disabled={busy}
          aria-label="Close"
        >
          ×
        </button>

        {phase === "choice" && (
          <>
            <header className="duc-header">
              <h2 id="duc-title" className="duc-title">
                Unlock <em>this drop</em>
              </h2>
              <p className="duc-sub">Choose how you&apos;d like to unlock</p>
            </header>

            <div className="duc-body">
              <button
                type="button"
                className="duc-primary"
                onClick={startSingleUnlock}
                disabled={busy}
              >
                {busy ? "Starting…" : `Unlock just this drop · ${priceText}`}
              </button>

              <p className="duc-or">
                Or subscribe for ongoing access to all {possessiveName(firstName)} drops
              </p>

              <div className="duc-tiers">
                {TIER_ORDER.map((id) => {
                  const tier = SUBSCRIPTION_TIERS[id];
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`duc-tier-card${tier.recommended ? " duc-tier-card--rec" : ""}`}
                      onClick={() => handleSubscribeTier(id)}
                      disabled={busy}
                    >
                      <div className="duc-tier-top">
                        <span className="duc-tier-name">{tier.name}</span>
                        {tier.recommended && (
                          <span className="duc-tier-badge">Recommended</span>
                        )}
                      </div>
                      <div className="duc-tier-price">
                        {formatTierDollars(tier.amountCents)}
                        <span>/month</span>
                      </div>
                      <span className="duc-tier-cta">Tap to subscribe</span>
                    </button>
                  );
                })}
              </div>

              {error && <p className="duc-error">{error}</p>}

              <div className="duc-footer">
                <button
                  type="button"
                  className="duc-later"
                  onClick={handleDismiss}
                  disabled={busy}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "payment" && !clientSecret && (
          <>
            <header className="duc-header">
              <h2 id="duc-title" className="duc-title">
                {kind === "archive" ? "Unlock the archive" : "Unlock this drop"}
              </h2>
              <p className="duc-sub">{busy ? "Starting checkout…" : "Preparing payment…"}</p>
            </header>
            <div className="duc-body">
              {error && <p className="duc-error">{error}</p>}
              {error && (
                <button type="button" className="duc-primary" onClick={startSingleUnlock} disabled={busy}>
                  Try again
                </button>
              )}
            </div>
          </>
        )}

        {phase === "payment" && clientSecret && stripePromise && (
          <>
            <header className="duc-header">
              <h2 id="duc-title" className="duc-title">
                Pay <em>{priceLabelFromCents(amountCents)}</em>
              </h2>
              <p className="duc-sub">
                {kind === "archive"
                  ? "Archive access · CAD · no rank badge"
                  : "One-time unlock · CAD · card only"}
              </p>
            </header>
            <div className="duc-body">
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#C4790A",
                      colorBackground: "#FFFFFF",
                      colorText: "#0E0C0A",
                      borderRadius: "10px",
                      fontFamily: "Inter, system-ui, sans-serif",
                    },
                  },
                }}
              >
                <PaymentForm
                  email={currentUser?.email}
                  onReady={setPaymentReady}
                  onStripeRefs={setStripeRef}
                />
              </Elements>

              {error && <p className="duc-error">{error}</p>}

              <button
                type="button"
                className="duc-primary"
                onClick={confirmPayment}
                disabled={busy || !paymentReady}
              >
                {busy ? "Processing…" : `Pay ${priceLabelFromCents(amountCents)}`}
              </button>

              <div className="duc-footer">
                <button
                  type="button"
                  className="duc-later"
                  onClick={() => {
                    if (busy) return;
                    setPhase("choice");
                    setClientSecret(null);
                    setError("");
                  }}
                  disabled={busy}
                >
                  ← Back
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "payment" && (!clientSecret || !stripePromise) && (
          <div className="duc-body">
            <p className="duc-error">
              {error
                || (!publishableKey
                  ? "Add REACT_APP_STRIPE_PUBLISHABLE_KEY to enable Stripe Payment Element."
                  : "Loading payment…")}
            </p>
            <button type="button" className="duc-later" onClick={handleDismiss}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
